import ContentContainer from 'components/content-container/content-container';
import { TradeSearchFilter } from 'components/trade-search-filter';
import { useUniqueData } from 'context/unique-data-context/unique-data-context';
import React from 'react';
import {
  Checkbox,
  Container,
  Divider,
  Grid,
  Header,
  Icon,
  Label,
  Message,
  Popup,
  Segment,
  Select,
} from 'semantic-ui-react';
import { GlobalStats } from './global-stats/global-stats';
import { CallsGraph } from './calls-graph';
import { CallsSourceGraph } from './calls-source-graph';
import { FinancialStats } from './financial-stats/financial-stats';
import SmsReceivedGraphRepository from './sms-received-graph/sms-received-graph-repository';
import { ProReviewInvitationsGraphRepository } from './pro-review-invitations-graph/pro-review-invitations-graph-repository';
import { SolicitationsGraph } from './solicitations-graph';
import { useCurrentBilikZones } from 'context/current-bilik-zones-context';
import { ProContactsGraphs } from './pro-contacts-graphs';

const DashboardContent: React.FC<{
  currentBilikZoneIds: number[] | undefined;
  proViewIds: number[] | undefined;
  proViewPublishedIds: number[] | undefined;
  tradeId: number | null | undefined;
  year: number | null | undefined;
  setTradeId: (tradeId: number | undefined) => void;
  setYear: (year: number | undefined) => void;
  compareYear: number | null | undefined;
  setCompareYear: (year: number | undefined) => void;
  yearOptions: any[];
}> = ({
  currentBilikZoneIds,
  proViewIds,
  proViewPublishedIds,
  tradeId,
  year,
  compareYear,
  yearOptions,
  setTradeId,
  setYear,
  setCompareYear,
}) => {
  const { showUniqueData, setShowUniqueData } = useUniqueData();
  const { currentBilikZones } = useCurrentBilikZones();
  return (
    <>
      <div style={{ marginBottom: '15px', marginLeft: '-12px' }}>
        <TradeSearchFilter
          tradeId={tradeId ?? undefined}
          onDelete={(): void => {
            setTradeId(undefined);
          }}
          onResultSelect={(event, { result }): void => {
            setTradeId(result.id);
          }}
        />
      </div>
      {proViewIds?.length && proViewPublishedIds?.length ? (
        <ContentContainer>
          <GlobalStats
            filters={{
              tradeId: tradeId ?? undefined,
              proViewIds: proViewIds,
              bilikZoneIds: currentBilikZoneIds,
            }}
          />
          <Segment.Group>
            <Segment>
              <Container fluid textAlign="right">
                <Select
                  value={year ?? yearOptions[0].value}
                  selection
                  options={yearOptions}
                  onChange={(event, { value }): void =>
                    setYear(value as number)
                  }
                />
                <Label>vs</Label>
                <Select
                  value={compareYear ?? yearOptions[1].value}
                  selection
                  options={yearOptions}
                  onChange={(event, { value }): void =>
                    setCompareYear(value as number)
                  }
                />
              </Container>
              <Divider hidden />
            </Segment>
            <ProContactsGraphs
              bilikZoneIds={currentBilikZoneIds}
              proViewIds={proViewIds}
              tradeId={tradeId ?? undefined}
              year={year ?? undefined}
              compareYear={compareYear ?? undefined}
            />
            <Segment>
              <Grid columns={2}>
                <Grid.Column>
                  <Header>Évolution des appels vers les pros</Header>
                </Grid.Column>

                <Grid.Column
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Header>Source des appels</Header>
                  <Checkbox
                    label="Unique"
                    checked={showUniqueData}
                    onChange={() => setShowUniqueData(!showUniqueData)}
                    toggle
                    style={{ marginRight: '24px' }}
                  />
                  <Popup
                    position="left center"
                    trigger={
                      <div
                        style={{
                          display: 'inline-block',
                          marginLeft: 5,
                          paddingBottom: 10,
                        }}
                      >
                        <Icon name="info circle" />
                      </div>
                    }
                    content="Compte seulement les contacts uniques par pro et par canal (appel/mail). Ex: Si un particulier contacte un pro 3 fois par téléphone, puis un second 2 fois par email, il ne sera compté que 2 fois."
                  />
                </Grid.Column>
              </Grid>
            </Segment>

            <Segment>
              <Grid>
                <Grid.Row columns={2}>
                  <Grid.Column width={8}>
                    <CallsGraph
                      bilikZoneIds={currentBilikZoneIds}
                      proViewIds={proViewIds}
                      year={year ?? undefined}
                      compareYear={compareYear ?? undefined}
                    />
                  </Grid.Column>

                  <Grid.Column width={8}>
                    <CallsSourceGraph
                      bilikZoneIds={currentBilikZoneIds}
                      proViewIds={proViewIds}
                      year={year ?? undefined}
                      unique={showUniqueData}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
            <Segment>
              <Grid columns={1}>
                <Grid.Column>
                  <Header>Évolution des demandes Mail</Header>
                </Grid.Column>
                <Grid.Column>
                  <SolicitationsGraph
                    bilikZoneIds={currentBilikZoneIds}
                    tradeId={tradeId ?? undefined}
                    year={year ?? undefined}
                    compareYear={compareYear ?? undefined}
                  />
                </Grid.Column>
              </Grid>
            </Segment>
            <Segment>
              <Grid columns={2}>
                <Grid.Column>
                  <Header>Évolution des demandes sms</Header>
                </Grid.Column>
                <Grid.Column>
                  <Header>Évolution des demandes d&apos;avis</Header>
                </Grid.Column>
              </Grid>
            </Segment>
            <Segment>
              <Grid columns={2}>
                <Grid.Column>
                  <SmsReceivedGraphRepository
                    bilikZoneIds={currentBilikZoneIds}
                    proViewIds={proViewIds}
                    year={year ?? undefined}
                    compareYear={compareYear ?? undefined}
                  />
                </Grid.Column>
                <Grid.Column>
                  <ProReviewInvitationsGraphRepository
                    bilikZoneIds={currentBilikZoneIds}
                    tradeId={tradeId ?? undefined}
                    year={year ?? undefined}
                    compareYear={compareYear ?? undefined}
                  />
                </Grid.Column>
              </Grid>
            </Segment>
            {currentBilikZoneIds ? (
              <>
                <Segment>
                  <Grid columns={2}>
                    <Grid.Column>
                      <Header>
                        Évolution des appels vers Bilik{' '}
                        {currentBilikZones?.map((zone) => zone.name).join(', ')}
                      </Header>
                    </Grid.Column>
                  </Grid>
                </Segment>
                <Segment>
                  <Grid columns={2}>
                    <Grid.Column>
                      <CallsGraph
                        year={year ?? undefined}
                        filters={[
                          {
                            'actors.bilik.bilikZone.id': {
                              $in: currentBilikZones?.map(
                                (bilikZone) => bilikZone.id,
                              ),
                            },
                          },
                        ]}
                      />
                    </Grid.Column>
                  </Grid>
                </Segment>
              </>
            ) : null}
          </Segment.Group>
          <FinancialStats
            bilikZoneIds={currentBilikZoneIds}
            proViewIds={proViewIds}
          />
        </ContentContainer>
      ) : (
        <Message>
          Aucun pro pour cette catégorie dans la zone sélectionnée
        </Message>
      )}
    </>
  );
};

export default DashboardContent;
